import { cn } from '@/lib/utils';

export interface LiveGroupAnchor {
  /** Stable section id (shortMarkets / closingSoon / today / thisWeek). */
  id: string;
  label: string;
}

export interface LiveGroupAnchorsProps {
  /** Visible groups only — a hidden (empty) group's chip is hidden with it (spec §4.3/§5.3). */
  anchors: LiveGroupAnchor[];
  /** The section currently in view (underlined), from {@link useActiveSection}. */
  activeId: string | null;
  /** Scrolls to the section — jump-to anchors, not filters. */
  onSelect: (id: string) => void;
}

/**
 * The group anchor chip row (Figma "live page"): Short Markets · Closing Soon
 * · Today · This Week. Tapping scrolls to the group; the in-view group's chip
 * carries the active underline.
 */
export function LiveGroupAnchors({ anchors, activeId, onSelect }: LiveGroupAnchorsProps) {
  if (anchors.length === 0) return null;

  return (
    <div className="flex w-full items-center gap-5 overflow-x-auto px-1 [scrollbar-width:none]">
      {anchors.map((anchor) => {
        const active = anchor.id === activeId;
        return (
          <button
            key={anchor.id}
            type="button"
            aria-current={active ? 'true' : undefined}
            onClick={() => onSelect(anchor.id)}
            className={cn(
              'shrink-0 border-none bg-transparent p-0 pb-1 text-sm font-medium cursor-pointer transition-colors',
              active ? 'text-accent underline decoration-2 underline-offset-8' : 'text-ink-muted hover:text-ink',
            )}
          >
            {anchor.label}
          </button>
        );
      })}
    </div>
  );
}
